import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of, throwError } from 'rxjs';
import { catchError, map, pluck, tap } from 'rxjs/operators';
import {
  AboutPro,
  AssignMultipleSlotData,
  AssignSlotData,
  DiscountData,
  Invoice,
  Payment,
  PaymentApiData,
  PaymentData,
  PaymentDiscountSubSlot,
  PaymentSlot,
  UpdateSlotData,
} from '../objects/payment';
import { APP_CONFIG } from './config.service';
import { PaymentService } from './payment.service';

@Injectable({
  providedIn: 'root',
})
export class PaymentApiService {
  private url = 'http://localhost:8080/api/';

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private paymentService: PaymentService,
  ) {
    APP_CONFIG()
      .pipe(pluck('baseUri'))
      .subscribe((url: string) => (this.url = url));
  }

  private pricelistBody(countryCode?: string) {
    return {
      prefferedLanguage: this.translate.currentLang,
      ...(countryCode ? { countryOfPurchase: countryCode } : {}),
    };
  }

  public getPayment(countryCode?: string) {
    return this.http
      .post(this.url + 'payments/pricelist/all', this.pricelistBody(countryCode))
      .pipe(
        map((paymentData: Payment) => this.handlePaymentSuccess(paymentData)),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public getPaymentSlots(countryCode?: string) {
    return this.http
      .post(this.url + 'payments/pricelist/slots', this.pricelistBody(countryCode))
      .pipe(
        map((paymentData: Payment) => this.handlePaymentSuccess(paymentData)),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public getPaymentApi(countryCode?: string) {
    return this.http
      .post(this.url + 'payments/pricelist/premium-api', this.pricelistBody(countryCode))
      .pipe(
        map((paymentData: Payment) => this.handlePaymentSuccess(paymentData)),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public getExtendPaymentApi(premiumId: number, countryCode?: string) {
    return this.http
      .post(
        this.url + `payments/pricelist/premium-api/${premiumId}/renewal`,
        this.pricelistBody(countryCode),
      )
      .pipe(
        map((paymentData: Payment) => this.handlePaymentSuccess(paymentData)),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public sendPayment(paymentData: PaymentData) {
    return this.http.post(this.url + 'payments/pay/subscription', paymentData).pipe(
      tap((paymentOrder: { link: string }) => this.handleSendSuccess(paymentOrder)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public sendPaymentSlots(paymentData: PaymentData) {
    return this.http.post(this.url + 'payments/pay/slots', paymentData).pipe(
      tap((paymentOrder: { link: string }) => this.handleSendSuccess(paymentOrder)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public sendPaymentApi(paymentData: PaymentApiData) {
    return this.http.post(this.url + 'payments/pay/premium-api', paymentData).pipe(
      tap((paymentOrder: { link: string }) => this.handleSendSuccess(paymentOrder)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public sendExtendPaymentApi(paymentData: PaymentApiData, premiumId: number) {
    return this.http
      .post(this.url + `payments/pay/premium-api/${premiumId}/renewal`, paymentData)
      .pipe(
        tap((paymentOrder: { link: string }) => this.handleSendSuccess(paymentOrder)),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public getDiscount(discountData: DiscountData) {
    return this.http.post(this.url + 'payments/discount', discountData).pipe(
      map((discount: PaymentDiscountSubSlot) => discount),
      catchError((error: HttpErrorResponse) => this.handleErrorWithoutReload(error)),
    );
  }

  public getInvoices() {
    return this.http.get(this.url + 'invoice').pipe(
      map((invoicesData: Invoice[]) => invoicesData),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public getAboutPro() {
    return this.http.get(this.url + 'payments/premium/info').pipe(
      map((aboutProData: AboutPro) => this.handleProSuccess(aboutProData)),
      catchError((error: HttpErrorResponse) => this.handleErrorWithoutReload(error)),
    );
  }

  public cancelSubscription(lang: string) {
    return this.http.post(this.url + 'premium-group/subscription/cancel', { lang }).pipe(
      map((cancelData) => cancelData),
      catchError((error: HttpErrorResponse) => this.handleErrorWithoutReload(error)),
    );
  }

  public cancelSubscriptionAccept(code: string) {
    return this.http
      .post(this.url + 'premium-group/subscription/cancellation/accept', { code })
      .pipe(
        map((cancelData) => cancelData),
        catchError((error: HttpErrorResponse) => this.handleErrorWithoutReload(error)),
      );
  }

  public assignSlot(assignSlotData: AssignSlotData) {
    return this.http.post(this.url + 'premium-group/slot/assign', assignSlotData).pipe(
      map((data: { slots: PaymentSlot[]; isRegistered: boolean }) => data),
      catchError((error: HttpErrorResponse) => this.handleErrorWithoutReload(error)),
    );
  }

  public assignMultipleSlot(assignSlotData: AssignMultipleSlotData) {
    return this.http.post(this.url + 'premium-group/slot/assign/bulk', assignSlotData).pipe(
      map((data) => data),
      catchError((error: HttpErrorResponse) => this.handleErrorWithoutReload(error)),
    );
  }

  public acceptAssignedEmail(code: string) {
    return this.http.post(this.url + 'premium-group/slot/accept', { code }).pipe(
      map((data: { isRegistered: boolean }) => data.isRegistered),
      catchError(() => of(null)),
    );
  }

  public updateSlot(updateSlotData: UpdateSlotData) {
    return this.http.post(this.url + 'premium-group/slot/update', updateSlotData).pipe(
      map((slots: PaymentSlot[]) => this.handleSlotsSuccess(slots)),
      catchError((error: HttpErrorResponse) => this.handleErrorWithoutReload(error)),
    );
  }

  public clearSlot(slotId: number) {
    return this.http.post(this.url + 'premium-group/slot/clear', { slotId }).pipe(
      map((slots: PaymentSlot[]) => this.handleSlotsSuccess(slots)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public giveRole(date: { slotId: number; lang: string }) {
    return this.http.post(this.url + 'premium-group/slot/role/change', date).pipe(
      map((message: string) => message),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public acceptRoleChange(date: { code: string; lang: string }) {
    return this.http.post(this.url + 'premium-group/slot/role/accept', date).pipe(
      map((result: { message: string }) => result.message),
      catchError(() => of(null)),
    );
  }

  public changeAdmin(date: { lang: string; email: string }) {
    return this.http.post(this.url + 'premium-group/owner/change', date).pipe(
      map((message: string) => message),
      catchError((error: HttpErrorResponse) => this.handleErrorWithoutReload(error)),
    );
  }

  public acceptAdminChangeVerify(date: { code: string; lang: string }) {
    return this.http.post(this.url + 'premium-group/owner/change/verify', date).pipe(
      map((result) => result),
      catchError((error: HttpErrorResponse) => this.handleErrorWithoutReload(error)),
    );
  }
  public acceptAdminChangeAccept(code: string) {
    return this.http.post(this.url + 'premium-group/owner/change/accept', { code }).pipe(
      map((result) => result),
      catchError((error: HttpErrorResponse) => this.handleErrorWithoutReload(error)),
    );
  }

  private handleProSuccess(pro: AboutPro) {
    this.paymentService.setProState(pro);
    return pro;
  }

  private handlePaymentSuccess(paymentData: Payment) {
    this.paymentService.setPaymentState(paymentData);
    return paymentData;
  }

  private handleSlotsSuccess(slots: PaymentSlot[]) {
    const aboutPro = this.paymentService.getProState();
    aboutPro.slots = slots;
    this.paymentService.setProState(aboutPro);
  }

  private handleSendSuccess(paymentOrder: { link: string }) {
    window.open(paymentOrder.link, '_self');
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 401) {
      this.router.navigate([this.translate.currentLang, 'auth'], {
        relativeTo: this.route,
      });
    } else if (error.status === 400 || error.status === 403) {
      window.location.reload();
    }
    return throwError('Error happend');
  }

  private handleErrorWithoutReload(error: HttpErrorResponse) {
    if (error.status === 401) {
      this.router.navigate([this.translate.currentLang, 'auth'], {
        relativeTo: this.route,
      });
    }
    return throwError(error);
  }
}
